import React, { useState, useEffect, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import DomPurify from 'dompurify';
import { Channel } from 'pusher-js';
import { ButtonClassNames, GlobalRouteNames, LocalStorageItems, WsEvents } from '../../../models/Common';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { useDateLocalization } from '../../../hooks/useDateLocalization';
import { useAppDispatch } from '../../../hooks/redux';
import { userInfoSlice } from '../../../store/reducers/UserInfoSlice';
import { paymentsApi } from '../../../services/apiServices/user/payments';
import { CustomerOrderStatuses } from '../../../constants/configuration';
import Wss from '../../../core/pusher/Pusher';
import { parseJwt } from '../../../utils';
import { OrderProcessing } from '../OrderProcessing';

export const ServiceActivated = memo(() => {
  const { t } = useTranslation();
  const { formatDate } = useDateLocalization();
  const [processing, setProcessing] = useState<boolean>();
  const channelRef = useRef<Channel | null>(null);
  const channelNameRef = useRef<string | null>(null);
  const { search } = window.location;
  const [fetchOrderInfo] = paymentsApi.useLazyGetOrdersInfoQuery();
  const history = useHistory();
  const { updateUserBalance } = userInfoSlice.actions;
  const dispatch = useAppDispatch();
  const sanitizer = DomPurify.sanitize;

  const navigateToMainPage = () => {
    history.push(GlobalRouteNames.HomePage);
  };
  const navigateToPaymentHistoryPage = () => {
    history.push(GlobalRouteNames.PaymentHistory);
  };

  const serviceName = localStorage.getItem(LocalStorageItems.ServiceName);
  const toDate = localStorage.getItem(LocalStorageItems.ToDate) || '';

  const formattedToDate = toDate
    ? formatDate(toDate, { day: '2-digit', month: '2-digit', year: '2-digit', locale: 'uk' })
    : '';

  const listenPaymentComplete = () => {
    const token = localStorage.getItem(LocalStorageItems.AccessToken);
    const { sub } = parseJwt(token);
    const pusher = Wss.getInstance();

    const channelName = `customer.${sub}`;
    const channel = pusher.subscribe(channelName);

    const cleanupChannel = () => {
      channel.unbind_all();
      pusher.unsubscribe(channelName);

      channelRef.current = null;
      channelNameRef.current = null;
    };

    channel.bind(WsEvents.PaymentComplete, (data) => {
      dispatch(updateUserBalance(data));
      setProcessing(false);
      cleanupChannel();
    });

    channel.bind(WsEvents.PaymentError, (data) => {
      cleanupChannel();
      history.push({
        pathname: GlobalRouteNames.UnknownError,
        state: { message: data.failureReason },
      });
    });

    channelRef.current = channel;
    channelNameRef.current = channelName;
  };

  const handleOrderInfo = async (orderCode: string, isInitialLoad = false) => {
    const { data } = await fetchOrderInfo(orderCode, false);

    if (!data?.length) {
      history.push(GlobalRouteNames.UnknownError);
      return;
    }
    const order = data[0];

    if (order.Status === CustomerOrderStatuses.ERROR) {
      history.push({
        pathname: GlobalRouteNames.UnknownError,
        state: { message: order.FailureReason },
      });
      return;
    }
    if (isInitialLoad) {
      if (order.Status !== CustomerOrderStatuses.PENDING) {
        setProcessing(false);
      }
      return;
    }
    if (order.Status === CustomerOrderStatuses.PROCESSED) {
      setProcessing(false);
      return;
    }
    history.push(GlobalRouteNames.UnknownError);
  };

  const onLoad = async () => {
    if (!serviceName) {
      history.push(GlobalRouteNames.HomePage);
      return;
    }
    const queryParams = new URLSearchParams(search);
    const orderCode = queryParams.get('orderCode');
    if (orderCode) {
      setProcessing(true);
      listenPaymentComplete();
      await handleOrderInfo(orderCode, true);
    }
  };

  useEffect(() => {
    let timerId;

    if (processing) {
      timerId = setTimeout(async () => {
        const queryParams = new URLSearchParams(search);
        const orderCode = queryParams.get('orderCode');
        if (orderCode) {
          await handleOrderInfo(orderCode);
        } else {
          history.push(GlobalRouteNames.UnknownError);
        }
      }, 15000);
    }
    if (processing === false) {
      localStorage.removeItem(LocalStorageItems.ServiceName);
    }
    return () => clearTimeout(timerId);
  }, [processing]);

  useEffect(() => {
    onLoad();
    return () => {
      localStorage.removeItem(LocalStorageItems.ServiceName);
      localStorage.removeItem(LocalStorageItems.FromDate);
      localStorage.removeItem(LocalStorageItems.ToDate);

      const pusher = Wss.getInstance();
      if (channelRef.current) {
        channelRef.current.unbind_all();
      }
      if (channelNameRef.current) {
        pusher.unsubscribe(channelNameRef.current);
      }
      channelRef.current = null;
      channelNameRef.current = null;
    };
  }, []);

  if (!serviceName) return null;

  if (processing) {
    return <OrderProcessing />;
  }

  return (
    <div className="service-activated">
      <div className="service-activated__header">
        <h1>{t('success.theServiceHasBeenSuccessfullyActivated')}</h1>
        <p
          className="success-text"
          dangerouslySetInnerHTML={{
            __html: sanitizer(t('success.yourServiceActivated', { to: formattedToDate })),
          }}
        />
      </div>
      <div className="service-activated__buttons-group">
        <CustomMUIButton
          label={t('buttons.toMain')}
          handleClick={navigateToMainPage}
          className={ButtonClassNames.SecondaryOutlined}
          isBold
        />
        <CustomMUIButton
          label={t('buttons.yourPayments')}
          handleClick={navigateToPaymentHistoryPage}
          className={ButtonClassNames.PrimaryFulfilled}
          isBold
        />
      </div>
    </div>
  );
});
